import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Save, Loader2, ShieldAlert, AlertTriangle, Skull, Info } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetSettings,
  adminUpdateSettings,
  adminSetFeatureFlag,
  adminNuclearWipeDryRun,
  adminNuclearWipeExecute,
  clearAdminToken,
  clearSession,
} from "@/lib/afritask";
import { formatApiError } from "@/lib/errors";

export default function AdminSettings() {
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [flagBusy, setFlagBusy] = useState({});

  const load = async () => {
    try {
      const d = await adminGetSettings();
      const normalized = _normalizeForUi(d.config);
      setCfg(normalized);
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load settings");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      // Sanity guards before submit — block impossible values
      const rr = cfg?.referral_rewards || {};
      const rate = Number(rr.revenue_share_rate_pct);
      const days = Number(rr.revenue_share_window_days);
      const pts = Number(rr.topup_bonus_points);
      if (Number.isNaN(rate) || rate < 0 || rate > 100) {
        toast.error("Revenue share must be between 0 and 100%");
        setBusy(false);
        return;
      }
      if (Number.isNaN(days) || days < 1 || days > 365) {
        toast.error("Revenue share window must be between 1 and 365 days");
        setBusy(false);
        return;
      }
      if (Number.isNaN(pts) || pts < 0) {
        toast.error("Top-up bonus points must be 0 or greater");
        setBusy(false);
        return;
      }
      const payload = _denormalizeForApi(cfg);
      await adminUpdateSettings(payload);
      toast.success("Settings saved — new requests will use these values");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  const set = (path, val) => {
    setCfg((c) => {
      const clone = JSON.parse(JSON.stringify(c));
      const parts = path.split(".");
      let node = clone;
      for (let i = 0; i < parts.length - 1; i++) node = node[parts[i]];
      node[parts[parts.length - 1]] = val;
      return clone;
    });
    setDirty(true);
  };

  // Feature-flag toggles auto-save on flip (independent of the dirty-banner
  // batch Save used for numeric fields). Optimistic update + revert on error.
  const toggleFlag = async (key, next) => {
    const prev = !!(cfg?.feature_flags?.[key]);
    // Optimistic update — does NOT set dirty
    setCfg((c) => {
      const clone = JSON.parse(JSON.stringify(c));
      clone.feature_flags = { ...(clone.feature_flags || {}), [key]: next };
      return clone;
    });
    setFlagBusy((s) => ({ ...s, [key]: true }));
    try {
      const r = await adminSetFeatureFlag(key, next);
      // Reconcile with server-persisted value
      setCfg((c) => {
        const clone = JSON.parse(JSON.stringify(c));
        clone.feature_flags = { ...(clone.feature_flags || {}), [key]: !!r.value };
        return clone;
      });
    } catch (e) {
      // Revert on failure
      setCfg((c) => {
        const clone = JSON.parse(JSON.stringify(c));
        clone.feature_flags = { ...(clone.feature_flags || {}), [key]: prev };
        return clone;
      });
      toast.error(formatApiError(e, "Failed to update flag"));
    } finally {
      setFlagBusy((s) => ({ ...s, [key]: false }));
    }
  };

  if (!cfg) return <p className="text-sm text-neutral-500">Loading…</p>;

  const fl = cfg.feature_flags || {};

  return (
    <div className="space-y-6">
      {dirty && (
        <Card className="border-amber-200 bg-amber-50" data-testid="settings-dirty-banner">
          <CardContent className="flex items-center justify-between p-4 text-sm">
            <span className="inline-flex items-center gap-2 text-amber-800">
              <ShieldAlert className="h-4 w-4" /> Unsaved changes.
            </span>
            <Button size="sm" onClick={() => setConfirmOpen(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="settings-save-btn">
              <Save className="mr-1 h-3.5 w-3.5" /> Save
            </Button>
          </CardContent>
        </Card>
      )}

      <Section title="Withdrawals" desc="Minimum withdrawal threshold.">
        <NumberField label="Minimum withdrawal (₦)" value={cfg.min_withdrawal_ngn} onChange={(v) => set("min_withdrawal_ngn", v)} min={100} step={100} testid="setting-min-withdrawal" />
      </Section>

      <Section title="Top-up tiers" desc="Naira → Work Points conversion.">
        {Object.entries(cfg.topup_tiers).map(([ngn, pts]) => (
          <NumberField
            key={ngn}
            label={`₦${Number(ngn).toLocaleString()} → pts`}
            value={pts}
            onChange={(v) => set(`topup_tiers.${ngn}`, v)}
            min={1}
            testid={`setting-tier-${ngn}`}
          />
        ))}
      </Section>

      <Section
        title="Referral rewards"
        desc="Rewards to the referrer when their referee tops up + earns."
      >
        <NumberField
          label="Flat top-up bonus (pts)"
          value={cfg.referral_rewards.topup_bonus_points}
          onChange={(v) => set("referral_rewards.topup_bonus_points", v)}
          min={0}
          step={1}
          testid="setting-referral-topup-bonus"
        />
        <p className="-mt-2 text-xs text-neutral-500 sm:pl-[calc(66.66%+0.5rem)]">
          Points awarded to referrer when their referee makes their first Paystack top-up.
        </p>

        <NumberField
          label="Revenue share (%)"
          value={cfg.referral_rewards.revenue_share_rate_pct}
          onChange={(v) => set("referral_rewards.revenue_share_rate_pct", parseFloat(v))}
          min={0}
          max={100}
          step={0.5}
          testid="setting-referral-revshare-rate"
        />
        <p className="-mt-2 text-xs text-neutral-500 sm:pl-[calc(66.66%+0.5rem)]">
          Currently: <span className="font-semibold text-neutral-700">
            {Number(cfg.referral_rewards.revenue_share_rate_pct || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%
          </span> of every ₦ your referee earns for the first{" "}
          <span className="font-semibold text-neutral-700">
            {Number(cfg.referral_rewards.revenue_share_window_days || 0)}
          </span>{" "}
          days.
        </p>

        <NumberField
          label="Revenue share window (days)"
          value={cfg.referral_rewards.revenue_share_window_days}
          onChange={(v) => set("referral_rewards.revenue_share_window_days", v)}
          min={1}
          max={365}
          step={1}
          testid="setting-referral-revshare-days"
        />
        <p className="-mt-2 text-xs text-neutral-500 sm:pl-[calc(66.66%+0.5rem)]">
          How many days after referee signup the revenue share applies.
        </p>
      </Section>

      <Section title="Selection algorithm" desc="Bucket percentages must sum to 1.0.">
        <NumberField label="Bucket A (Elite) %" value={cfg.selection.bucket_A_pct} onChange={(v) => set("selection.bucket_A_pct", parseFloat(v))} step={0.01} min={0} max={1} testid="setting-bucket-A" />
        <NumberField label="Bucket B (Loyal) %" value={cfg.selection.bucket_B_pct} onChange={(v) => set("selection.bucket_B_pct", parseFloat(v))} step={0.01} min={0} max={1} testid="setting-bucket-B" />
        <NumberField label="Bucket C (New)   %" value={cfg.selection.bucket_C_pct} onChange={(v) => set("selection.bucket_C_pct", parseFloat(v))} step={0.01} min={0} max={1} testid="setting-bucket-C" />
        <NumberField label="Minimum slots per task" value={cfg.selection.min_slots} onChange={(v) => set("selection.min_slots", v)} step={1} min={1} testid="setting-min-slots" />
      </Section>

      <Section title="Feature flags" desc="Careful — production toggles. Changes save instantly.">
        <div className="space-y-2">
          {[
            ["enable_transfers", "Paystack Transfers auto-approve", null],
            ["enable_signup", "Allow new user signups", null],
            ["maintenance_mode", "Maintenance mode (users see banner)", null],
            ["waitlist_mode", "Waitlist mode", "New signups join the waitlist instead of the dashboard. Existing users unaffected."],
          ].map(([k, l, helper]) => (
            <div key={k} className="rounded-lg border border-neutral-200 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-neutral-800">{l}</p>
                <Switch
                  checked={!!fl[k]}
                  onCheckedChange={(v) => toggleFlag(k, v)}
                  disabled={!!flagBusy[k]}
                  className="data-[state=checked]:bg-[#B45309] data-[state=unchecked]:bg-neutral-300"
                  data-testid={`toggle-${k}`}
                />
              </div>
              {helper && <p className="mt-1.5 text-xs text-neutral-500">{helper}</p>}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Waitlist" desc="Controls the launch queue.">
        <NumberField
          label="Launch goal (signups)"
          value={cfg.waitlist?.launch_goal ?? 100}
          onChange={(v) => set("waitlist.launch_goal", v)}
          min={1}
          step={1}
          testid="setting-waitlist-launch-goal"
        />
        <p className="-mt-2 text-xs text-neutral-500 sm:pl-[calc(66.66%+0.5rem)]">
          Target signup count shown on the waitlist page progress bar. Does not auto-launch.
        </p>
      </Section>

      {/* -------------------------------------- DANGER ZONE ------ */}
      <DangerZone />

      {/* Save-confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="settings-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Persist these changes?</DialogTitle>
            <DialogDescription>
              New requests will use these values. Existing withdrawals / tasks won&apos;t be recalculated retroactively.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-confirm-save">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <section>
      <div className="mb-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-xs text-neutral-500">{desc}</p>
      </div>
      <Card>
        <CardContent className="space-y-3 p-5">{children}</CardContent>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Config normalisation: convert `revenue_share_rate` (stored as fraction 0-1)
// into a display percentage 0-100 on load, and back to fraction on save.
// Also ensures `referral_rewards` exists with defaults so field access is safe
// on legacy configs that only had the old `referral_tiers` block.
// ---------------------------------------------------------------------------
const DEFAULT_REFERRAL_REWARDS = {
  topup_bonus_points: 10,
  revenue_share_rate: 0.05,
  revenue_share_window_days: 30,
};

function _normalizeForUi(config) {
  const c = JSON.parse(JSON.stringify(config || {}));
  const rr = { ...DEFAULT_REFERRAL_REWARDS, ...(c.referral_rewards || {}) };
  const rate = Number(rr.revenue_share_rate);
  c.referral_rewards = {
    topup_bonus_points: Number.isFinite(Number(rr.topup_bonus_points))
      ? Number(rr.topup_bonus_points)
      : DEFAULT_REFERRAL_REWARDS.topup_bonus_points,
    revenue_share_window_days: Number.isFinite(Number(rr.revenue_share_window_days))
      ? Number(rr.revenue_share_window_days)
      : DEFAULT_REFERRAL_REWARDS.revenue_share_window_days,
    // UI-only: percentage (0-100) — original fraction preserved separately for round-trip
    revenue_share_rate_pct: Number.isFinite(rate)
      ? Math.round(rate * 10000) / 100 // 0.075 -> 7.5, 0.05 -> 5
      : DEFAULT_REFERRAL_REWARDS.revenue_share_rate * 100,
  };
  // Seed `waitlist` block for legacy configs so `set("waitlist.launch_goal", …)`
  // dot-path traversal never crashes on undefined.
  c.waitlist = { launch_goal: 100, ...(c.waitlist || {}) };
  return c;
}

function _denormalizeForApi(config) {
  const c = JSON.parse(JSON.stringify(config || {}));
  const rr = c.referral_rewards || {};
  const pct = Number(rr.revenue_share_rate_pct);
  c.referral_rewards = {
    topup_bonus_points: Math.max(0, parseInt(rr.topup_bonus_points || 0, 10)),
    revenue_share_rate: Number.isFinite(pct) ? Math.round(pct * 10000) / 1000000 : 0.05, // 5 -> 0.05, 7.5 -> 0.075 (rounded to 4dp)
    revenue_share_window_days: Math.max(1, parseInt(rr.revenue_share_window_days || 1, 10)),
  };
  return c;
}

function NumberField({ label, value, onChange, min, max, step, testid }) {
  return (
    <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-3">
      <Label className="sm:col-span-2 text-sm font-normal text-neutral-700">{label}</Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(step && String(step).includes(".") ? parseFloat(e.target.value) : parseInt(e.target.value || "0", 10))}
        min={min}
        max={max}
        step={step}
        data-testid={testid}
        className="max-w-[180px] font-mono"
      />
    </div>
  );
}

// ============================================================================
// DANGER ZONE — Nuclear Wipe
// ============================================================================
const CONFIRM_PHRASE = "NUKE ALL DATA";

function DangerZone() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dryLoading, setDryLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [typedPhrase, setTypedPhrase] = useState("");
  const [wiping, setWiping] = useState(false);
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(5);

  const openDialog = async () => {
    setDialogOpen(true);
    setTypedPhrase("");
    setResult(null);
    setDryRun(null);
    setDryLoading(true);
    try {
      const d = await adminNuclearWipeDryRun();
      setDryRun(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wipe preview");
      setDialogOpen(false);
    } finally {
      setDryLoading(false);
    }
  };

  const closeDialog = () => {
    if (wiping) return;
    setDialogOpen(false);
  };

  const executeWipe = async () => {
    setWiping(true);
    try {
      const r = await adminNuclearWipeExecute();
      setResult(r);
      toast.success(`Wiped ${r.total_deleted.toLocaleString()} documents across ${r.collections_wiped} collections`);
      // 5-second countdown, then clear session + redirect
      let n = 5;
      setCountdown(n);
      const timer = setInterval(() => {
        n -= 1;
        setCountdown(n);
        if (n <= 0) {
          clearInterval(timer);
          clearAdminToken();
          clearSession();
          window.location.href = "/";
        }
      }, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
      setWiping(false);
    }
  };

  const totalDocs = dryRun?.total_documents ?? 0;
  const rows = dryRun?.documents_by_collection
    ? Object.entries(dryRun.documents_by_collection)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
    : [];
  const phraseValid = typedPhrase === CONFIRM_PHRASE;

  return (
    <section data-testid="danger-zone-section" className="pt-6">
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-red-700 inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Danger Zone
        </h2>
        <p className="text-xs text-neutral-500">Irreversible platform-wide actions. Read carefully.</p>
      </div>

      {/* Deploy notice banner */}
      <div
        data-testid="nuclear-deploy-notice"
        className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 inline-flex gap-2"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          <b>Deploy required.</b> This button only works after the app is redeployed.
          If you just added it, redeploy via Emergent → Deploy before it will appear on
          production at <span className="font-mono">afritaskai.ca</span>.
        </span>
      </div>

      <Card className="border-2 border-red-300 bg-red-50/40">
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-red-800">
                Nuclear Wipe — delete ALL platform data
              </p>
              <p className="mt-0.5 text-xs text-red-700/80">
                Wipes every user, task, entry, transaction, withdrawal, referral, and ledger row.
                Schema/indexes remain. <b>Cannot be undone.</b>
              </p>
            </div>
            <Button
              onClick={openDialog}
              data-testid="nuclear-wipe-open-btn"
              className="bg-red-600 hover:bg-red-700 text-white shrink-0"
            >
              <Skull className="mr-2 h-4 w-4" /> Nuclear Wipe
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(o) => (!o ? closeDialog() : null)}>
        <DialogContent data-testid="nuclear-wipe-dialog" className="max-w-lg">
          {!result ? (
            <>
              <DialogHeader>
                <DialogTitle className="inline-flex items-center gap-2 text-red-700">
                  <Skull className="h-5 w-5" /> Confirm Nuclear Wipe
                </DialogTitle>
                <DialogDescription>
                  This will permanently delete every document from every collection
                  in the connected database ({dryRun?.db_name || "…"}). This cannot be undone.
                </DialogDescription>
              </DialogHeader>

              {dryLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-neutral-600" data-testid="nuclear-wipe-loading">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading document counts…
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-lg border border-red-200 bg-white">
                    <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-800">
                      About to delete {totalDocs.toLocaleString()} document{totalDocs === 1 ? "" : "s"} across{" "}
                      {dryRun?.collections_count ?? 0} collection{dryRun?.collections_count === 1 ? "" : "s"}
                    </div>
                    <div className="max-h-56 overflow-y-auto p-2">
                      {rows.length === 0 ? (
                        <p className="p-3 text-center text-sm text-neutral-500" data-testid="nuclear-wipe-nothing">
                          All collections are already empty. Nothing to wipe.
                        </p>
                      ) : (
                        <table className="w-full text-xs" data-testid="nuclear-wipe-preview-table">
                          <tbody>
                            {rows.map(([name, count]) => (
                              <tr key={name} className="border-b border-neutral-100 last:border-0">
                                <td className="px-2 py-1.5 font-mono text-neutral-700">{name}</td>
                                <td className="px-2 py-1.5 text-right font-semibold text-red-700">
                                  {count.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-neutral-700">
                      Type <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-red-700">{CONFIRM_PHRASE}</span> to confirm
                    </Label>
                    <Input
                      value={typedPhrase}
                      onChange={(e) => setTypedPhrase(e.target.value)}
                      placeholder={CONFIRM_PHRASE}
                      data-testid="nuclear-wipe-phrase-input"
                      className="mt-1.5 font-mono"
                      disabled={wiping || totalDocs === 0}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={closeDialog}
                  disabled={wiping}
                  data-testid="nuclear-wipe-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeWipe}
                  disabled={!phraseValid || wiping || dryLoading || totalDocs === 0}
                  className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  data-testid="nuclear-wipe-execute-btn"
                >
                  {wiping ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wiping…
                    </>
                  ) : (
                    <>
                      <Skull className="mr-2 h-4 w-4" /> Wipe everything now
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <NukeResult result={result} countdown={countdown} />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function NukeResult({ result, countdown }) {
  const rows = Object.entries(result.deleted_by_collection || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="nuclear-wipe-result">
      <DialogHeader>
        <DialogTitle className="inline-flex items-center gap-2 text-emerald-700">
          <Skull className="h-5 w-5" /> Platform wiped
        </DialogTitle>
        <DialogDescription>
          {result.total_deleted.toLocaleString()} documents deleted across{" "}
          {result.collections_wiped} collections. All sessions revoked.
        </DialogDescription>
      </DialogHeader>
      <div className="my-3 max-h-56 overflow-y-auto rounded-lg border border-emerald-200 bg-white">
        {rows.length === 0 ? (
          <p className="p-3 text-center text-sm text-neutral-500">
            Database was already empty — nothing to delete.
          </p>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {rows.map(([name, count]) => (
                <tr key={name} className="border-b border-neutral-100 last:border-0">
                  <td className="px-2 py-1.5 font-mono text-neutral-700">{name}</td>
                  <td className="px-2 py-1.5 text-right font-semibold text-emerald-700">
                    -{count.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-center text-sm text-neutral-600" data-testid="nuclear-wipe-countdown">
        Redirecting to landing page in <b>{countdown}</b>s…
      </p>
    </div>
  );
}
